import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { LogOut, Mail } from "lucide-react";
import { api, apiError, fmtDate } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Switch } from "../components/ui/switch";
import { localeOf } from "../i18n";

export default function Profile() {
  const { t, i18n } = useTranslation();
  const { user, logout, setUser } = useAuth();
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const locale = localeOf(i18n.language);

  useEffect(() => { api.get("/me/summary").then(({ data }) => setSummary(data)).catch(() => {}); }, []);

  const toggleEmail = async (v) => {
    try {
      const { data } = await api.patch("/auth/profile", {
        notification_prefs: { ...(user.notification_prefs || {}), email: v },
      });
      setUser(data);
      toast.success(t("common.saved"));
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-5" data-testid="profile-page">
      <div className="vy-card p-5">
        <div className="flex items-center gap-4">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary font-display text-xl font-bold text-primary-foreground">
            {(user?.name || "?").slice(0, 2).toUpperCase()}
          </div>
          <div className="min-w-0">
            <h1 className="font-display text-xl font-bold" data-testid="profile-name">{user?.name}</h1>
            <p className="truncate text-sm text-muted-foreground">{user?.email}</p>
            <p className="text-xs font-semibold text-muted-foreground">
              {t(`admin.${user?.role}`)}{user?.team_name ? ` · ${user.team_name}` : ""}
            </p>
          </div>
        </div>
      </div>

      <div className="vy-card p-5" data-testid="profile-summary">
        <h2 className="font-display text-lg font-bold">{t("profile.myScore")}</h2>
        <p className="mt-2 font-display text-4xl font-extrabold text-primary" data-testid="profile-score">
          {summary?.score ?? 0}
        </p>
        <div className="mt-4 grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl bg-muted p-3">
            <p className="font-display text-xl font-bold">{summary?.events_attended ?? 0}</p>
            <p className="text-[11px] font-semibold text-muted-foreground">{t("home.attended")}</p>
          </div>
          <div className="rounded-xl bg-muted p-3">
            <p className="font-display text-xl font-bold">{summary?.missions_approved ?? 0}</p>
            <p className="text-[11px] font-semibold text-muted-foreground">{t("home.missions")}</p>
          </div>
          <div className="rounded-xl bg-muted p-3">
            <p className="font-display text-xl font-bold">{summary?.no_shows ?? 0}</p>
            <p className="text-[11px] font-semibold text-muted-foreground">{t("profile.noShows")}</p>
          </div>
        </div>
      </div>

      <div className="vy-card p-5" data-testid="profile-ledger">
        <h2 className="font-display text-lg font-bold">{t("profile.ledger")}</h2>
        <ul className="mt-3 divide-y divide-border">
          {(summary?.ledger || []).map((l) => (
            <li key={l.id} className="flex items-center gap-3 py-3">
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold">{l.reason}</p>
                <p className="text-xs text-muted-foreground">{fmtDate(l.created_at, locale)}</p>
              </div>
              <span className={`font-display text-base font-bold ${l.points >= 0 ? "text-[hsl(var(--success))]" : "text-destructive"}`}>
                {l.points >= 0 ? "+" : ""}{l.points}
              </span>
            </li>
          ))}
          {(summary?.ledger || []).length === 0 && (
            <li className="py-3 text-sm text-muted-foreground">{t("common.none")}</li>
          )}
        </ul>
      </div>

      <div className="vy-card flex items-center gap-3 p-5">
        <Mail className="h-5 w-5 text-muted-foreground" />
        <span className="flex-1 text-sm font-semibold">{t("profile.prefsEmail")}</span>
        <Switch data-testid="profile-email-pref-switch"
          checked={user?.notification_prefs?.email !== false}
          onCheckedChange={toggleEmail} />
      </div>

      <Button data-testid="profile-logout-btn" variant="outline"
        onClick={async () => { await logout(); navigate("/login"); }}
        className="h-14 w-full rounded-full font-bold">
        <LogOut className="mr-2 h-5 w-5" />{t("common.logout")}
      </Button>
    </div>
  );
}
